import {
  formatCurrency,
  formatNumber,
  formatPercent,
  formatCalculatedValue,
  getCalculatedFormatOptions,
  getCurrencyCodeFromRecord,
  normalizeCalculatedDisplayConfig,
  resolveTokenCurrency,
  toDisplayPrimitive,
} from './formatUtils';

/** Data types that should be right-aligned in report tables / PDF export. */
export const RIGHT_ALIGNED_REPORT_DATA_TYPES = [
  'number',
  'currency',
  'percent',
  'rollupsummary',
  'autonumber',
];

export function isRightAlignedReportDataType(dataType) {
  return RIGHT_ALIGNED_REPORT_DATA_TYPES.includes(
    String(dataType || '').toLowerCase()
  );
}

/** Calculated field effective return type from display_config. */
export function getCalculatedReturnType(field) {
  if (!field) return null;
  const dt = String(field.data_type || '').toLowerCase();
  if (dt !== 'calculated') return null;
  const normalized = normalizeCalculatedDisplayConfig(
    field.display_config || field.displayConfig
  );
  return normalized.returnType || 'text';
}

/**
 * Data type used for report summary dropdowns (Sum, Avg, etc.).
 * Calculated / rollup fields use display_config return / output type.
 */
export function resolveReportFieldSummaryDataType(field) {
  if (!field) return 'text';
  const dt = String(field.data_type || '').toLowerCase();
  if (dt === 'calculated') {
    return getCalculatedReturnType(field) || 'text';
  }
  if (dt === 'rollupsummary') {
    return getRollupOutputType(field) || 'number';
  }
  return dt;
}

/** Summary dropdown options for a report field (builder UI). */
/**
 * Find a preview/report row cell for a field (handles `object_field` SQL aliases).
 */
export function resolveReportRowFieldData(row, field, primaryObjectName = null) {
  if (!row || !field) return undefined;
  const fieldName = String(field.field_name || '').trim();
  if (!fieldName) return undefined;

  const primary = primaryObjectName ? String(primaryObjectName).trim() : '';
  const tableName = String(field.object_name || primary || 'main').trim();
  const fieldNameLower = fieldName.toLowerCase();

  const candidates = [];
  if (tableName) candidates.push(`${tableName}_${fieldName}`);
  if (primary && `${primary}_${fieldName}` !== `${tableName}_${fieldName}`) {
    candidates.push(`${primary}_${fieldName}`);
  }
  candidates.push(fieldName);
  if (tableName !== 'main') candidates.push(`main_${fieldName}`);

  const rowKeys = Object.keys(row);
  for (const key of candidates) {
    const exact = rowKeys.find(k => k === key);
    if (exact) return row[exact];
    const ci = rowKeys.find(k => k.toLowerCase() === key.toLowerCase());
    if (ci) return row[ci];
  }

  const suffixLower = `_${fieldNameLower}`;
  const matchedKey = rowKeys.find(k => {
    const kl = k.toLowerCase();
    return kl === fieldNameLower || kl.endsWith(suffixLower);
  });
  return matchedKey ? row[matchedKey] : undefined;
}

/**
 * Parse a raw / display value for numeric sort (strips currency symbols, commas, etc.).
 */
export function parseReportSortNumeric(raw) {
  if (raw === null || raw === undefined || raw === '') return null;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  const str = String(raw).trim();
  if (!str) return null;
  const cleaned = str.replace(/[^\d.-]/g, '');
  const n = Number(cleaned !== '' ? cleaned : str);
  return Number.isFinite(n) ? n : null;
}

/** Compare two sort keys (numbers, strings, nulls). */
export function compareReportSortValues(a, b) {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;

  const na =
    typeof a === 'number' && Number.isFinite(a) ? a : parseReportSortNumeric(a);
  const nb =
    typeof b === 'number' && Number.isFinite(b) ? b : parseReportSortNumeric(b);

  if (na !== null && nb !== null) return na - nb;
  if (na !== null) return -1;
  if (nb !== null) return 1;

  return String(a).localeCompare(String(b), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}

/** Whether a report field column should use numeric sort (ReportDataViewer). */
export function reportFieldSortsNumerically(field) {
  if (!field) return false;
  const dt = String(field.data_type || field.field_type || '').toLowerCase();
  if (['currency', 'number', 'percent'].includes(dt)) return true;
  if (dt === 'calculated' || dt === 'rollupsummary') {
    return reportFieldUsesZeroWhenEmpty(field);
  }
  return REPORT_NUMERIC_SUMMARY_TYPES.has(resolveReportFieldSummaryDataType(field));
}

function extractReportFieldRawForSort(fieldData) {
  if (fieldData === null || fieldData === undefined) {
    return { value: null, display: null };
  }
  if (typeof fieldData !== 'object' || Array.isArray(fieldData)) {
    return { value: fieldData, display: null };
  }
  return {
    value: fieldData.value,
    display: fieldData.displayValue,
  };
}

/** Collect all detail rows under a nested report group node. */
export function collectRowsUnderReportGroupNode(node) {
  if (Array.isArray(node)) return node;
  if (node && typeof node === 'object') {
    return Object.values(node).flatMap(collectRowsUnderReportGroupNode);
  }
  return [];
}

/** Resolve a report field from sort field id (field_config_id or report field id). */
export function findReportFieldForSort(fieldId, fields = []) {
  if (fieldId == null || !Array.isArray(fields)) return null;
  const id = String(fieldId);
  return (
    fields.find(
      f =>
        String(f.id) === id ||
        String(f.field_config_id) === id ||
        String(f.field_id) === id
    ) || null
  );
}

/**
 * Sort group bucket keys by aggregated measure (summary reports).
 */
export function sortReportGroupKeysByFieldAggregation(
  keys,
  groups,
  sortField,
  direction,
  summarizedFields,
  primaryObjectName = null,
  baseCurrencyCode = null,
  exchangeRateLookup = null
) {
  const summarized = summarizedFields?.[sortField?.id];
  const aggregation = summarized?.aggregation || 'sum';
  const dirMult = direction === 'desc' ? -1 : 1;
  return [...(keys || [])].sort((keyA, keyB) => {
    const rowsA = collectRowsUnderReportGroupNode(groups[keyA]);
    const rowsB = collectRowsUnderReportGroupNode(groups[keyB]);
    const va = getAggregationNumericResult(
      rowsA,
      sortField,
      aggregation,
      baseCurrencyCode,
      exchangeRateLookup,
      primaryObjectName
    );
    const vb = getAggregationNumericResult(
      rowsB,
      sortField,
      aggregation,
      baseCurrencyCode,
      exchangeRateLookup,
      primaryObjectName
    );
    const safeA = va === null || va === undefined ? 0 : va;
    const safeB = vb === null || vb === undefined ? 0 : vb;
    return dirMult * compareReportSortValues(safeA, safeB);
  });
}

/**
 * Client-side sort for flat report rows (runtime or saved config.sort).
 */
export function sortReportFlatRows(
  rows,
  sortField,
  direction,
  primaryObjectName = null
) {
  if (!Array.isArray(rows) || !sortField || rows.length === 0) return rows;
  const dirMult = direction === 'desc' || direction === 'DESC' ? -1 : 1;
  const sorted = [...rows];
  sorted.sort((a, b) => {
    const va = getReportRowFieldSortComparable(
      sortField,
      a,
      primaryObjectName
    );
    const vb = getReportRowFieldSortComparable(
      sortField,
      b,
      primaryObjectName
    );
    return dirMult * compareReportSortValues(va, vb);
  });
  return sorted;
}

/** Apply asc/desc to two comparables from {@link compareReportSortValues}. */
export function applySortDirection(sortOrder, aVal, bVal) {
  const dirMult = sortOrder === 'desc' || sortOrder === 'DESC' ? -1 : 1;
  return dirMult * compareReportSortValues(aVal, bVal);
}

/**
 * Comparable for related-list rows shaped as `{ fieldName, value, displayValue, type }`.
 */
export function getFieldsArrayCellSortComparable(
  fieldMeta,
  fieldDataTypesByName = {}
) {
  if (!fieldMeta) {
    return '';
  }
  const fieldName = fieldMeta.fieldName || fieldMeta.field_name || '';
  const dataType =
    fieldMeta.type ||
    fieldMeta.calculatedReturnType ||
    fieldDataTypesByName[fieldName] ||
    '';
  const displayConfig =
    fieldMeta.displayConfig ?? fieldMeta.display_config ?? null;
  return getListViewCellSortComparable(
    {
      value: fieldMeta.value,
      displayValue: fieldMeta.displayValue,
      type: fieldMeta.type,
      calculatedReturnType: fieldMeta.calculatedReturnType,
      displayConfig,
    },
    dataType,
    displayConfig,
    fieldName,
    fieldDataTypesByName
  );
}

/**
 * Sort records that expose `fields: [{ fieldName, value, displayValue, type }]`.
 */
export function sortFieldsArrayRecords(
  records,
  sortBy,
  sortOrder,
  fieldDataTypesByName = {}
) {
  if (!sortBy || !sortOrder || !Array.isArray(records) || records.length === 0) {
    return records;
  }
  const sorted = [...records];
  sorted.sort((a, b) => {
    if (sortBy === 'id') {
      const aId = a.fields?.find(f => f.fieldName === 'id');
      const bId = b.fields?.find(f => f.fieldName === 'id');
      const aVal = aId?.displayValue ?? aId?.value ?? '';
      const bVal = bId?.displayValue ?? bId?.value ?? '';
      return applySortDirection(sortOrder, aVal, bVal);
    }

    const aField = a.fields?.find(f => f.fieldName === sortBy);
    const bField = b.fields?.find(f => f.fieldName === sortBy);
    const sortType =
      aField?.type ||
      bField?.type ||
      fieldDataTypesByName[sortBy] ||
      '';

    if (sortType === 'date' || sortType === 'datetime') {
      const aRaw = aField?.value ?? aField?.displayValue ?? '';
      const bRaw = bField?.value ?? bField?.displayValue ?? '';
      const aDate = new Date(aRaw);
      const bDate = new Date(bRaw);
      if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
        return applySortDirection(sortOrder, aDate.getTime(), bDate.getTime());
      }
      return applySortDirection(
        sortOrder,
        String(aRaw).toLowerCase(),
        String(bRaw).toLowerCase()
      );
    }

    const emptyNumeric = reportFieldSortsNumerically({
      data_type: fieldDataTypesByName[sortBy],
    })
      ? 0
      : '';
    const aVal = aField
      ? getFieldsArrayCellSortComparable(aField, fieldDataTypesByName)
      : emptyNumeric;
    const bVal = bField
      ? getFieldsArrayCellSortComparable(bField, fieldDataTypesByName)
      : emptyNumeric;
    return applySortDirection(sortOrder, aVal, bVal);
  });
  return sorted;
}

/**
 * Sort list-view / ResizableTable row objects (field name keys → cell objects).
 */
export function sortListViewRecordRows(
  rows,
  sortBy,
  sortOrder,
  fieldDataTypes = {}
) {
  if (!sortBy || !sortOrder || !Array.isArray(rows) || rows.length === 0) {
    return rows;
  }
  const sorted = [...rows];
  sorted.sort((a, b) => {
    if (sortBy === 'id') {
      const aId = a.id?.value ?? a.id ?? '';
      const bId = b.id?.value ?? b.id ?? '';
      return applySortDirection(sortOrder, aId, bId);
    }

    const aField = a[sortBy];
    const bField = b[sortBy];
    const sortType =
      aField?.calculatedReturnType ||
      aField?.type ||
      bField?.calculatedReturnType ||
      bField?.type ||
      fieldDataTypes[sortBy] ||
      '';
    const sortDisplayConfig =
      aField?.displayConfig ??
      aField?.display_config ??
      bField?.displayConfig ??
      bField?.display_config ??
      null;

    if (sortType === 'date' || sortType === 'datetime') {
      const aRaw = aField?.value ?? aField?.displayValue ?? '';
      const bRaw = bField?.value ?? bField?.displayValue ?? '';
      const aDate = new Date(aRaw);
      const bDate = new Date(bRaw);
      if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
        return applySortDirection(sortOrder, aDate.getTime(), bDate.getTime());
      }
      return applySortDirection(
        sortOrder,
        String(aRaw).toLowerCase(),
        String(bRaw).toLowerCase()
      );
    }

    const aVal = getListViewCellSortComparable(
      aField,
      sortType,
      sortDisplayConfig,
      sortBy,
      fieldDataTypes
    );
    const bVal = getListViewCellSortComparable(
      bField,
      sortType,
      sortDisplayConfig,
      sortBy,
      fieldDataTypes
    );
    return applySortDirection(sortOrder, aVal, bVal);
  });
  return sorted;
}

const REPORT_NUMERIC_SUMMARY_TYPES = new Set([
  'number',
  'currency',
  'percent',
  'rollupsummary',
  'autonumber'
]);

/** Whether list/report cell type should sort as a single numeric sequence (incl. empty → 0). */
export function reportCellTypeSortsAsNumeric(dataType, displayConfig = null) {
  const dt = String(dataType || '').toLowerCase();
  if (dt === 'calculated') {
    const pseudo = { data_type: 'calculated', display_config: displayConfig };
    return reportFieldUsesZeroWhenEmpty(pseudo);
  }
  if (dt === 'rollupsummary') {
    const parsed = parseDisplayConfigForReport(displayConfig);
    const rollupCfg = parsed.rollupSummary || parsed.rollup_summary || parsed;
    const outputType = String(
      rollupCfg?.outputType || rollupCfg?.output_type || 'number'
    ).toLowerCase();
    return REPORT_ZERO_WHEN_EMPTY_TYPES.has(outputType);
  }
  return REPORT_ZERO_WHEN_EMPTY_TYPES.has(dt);
}

/** Resolve list-view field data type (cell.type, config map, or calculated return type). */
export function resolveListViewFieldDataType(
  fieldCell,
  fieldName = null,
  fieldDataTypes = null
) {
  if (fieldCell?.calculatedReturnType) {
    return String(fieldCell.calculatedReturnType).toLowerCase();
  }
  const fromCell = fieldCell?.type;
  if (fromCell) return String(fromCell).toLowerCase();
  if (fieldName && fieldDataTypes && fieldDataTypes[fieldName]) {
    return String(fieldDataTypes[fieldName]).toLowerCase();
  }
  return '';
}

/**
 * Sort comparable for list-view cells `{ value, displayValue, type }`.
 * Uses raw value first (not formatted display) so currency sorts numerically.
 */
export function getListViewCellSortComparable(
  fieldCell,
  dataType,
  displayConfig = null,
  fieldName = null,
  fieldDataTypes = null
) {
  const resolvedType = resolveListViewFieldDataType(
    fieldCell,
    fieldName,
    fieldDataTypes
  );
  const typeForNumeric =
    resolvedType ||
    (dataType ? String(dataType).toLowerCase() : '');

  if (!fieldCell) {
    return reportCellTypeSortsAsNumeric(typeForNumeric, displayConfig) ? 0 : '';
  }

  if (!reportCellTypeSortsAsNumeric(typeForNumeric, displayConfig)) {
    const text = fieldCell.displayValue ?? fieldCell.value ?? '';
    return String(text).toLowerCase();
  }

  const n =
    parseReportSortNumeric(fieldCell.value) ??
    parseReportSortNumeric(fieldCell.displayValue);
  return n !== null ? n : 0;
}

export function getReportRowFieldSortComparable(
  field,
  row,
  primaryObjectName = null
) {
  const sortsNumeric = reportFieldSortsNumerically(field);
  const fieldData = resolveReportRowFieldData(row, field, primaryObjectName);
  const effectiveType = resolveReportFieldSummaryDataType(field);

  if (sortsNumeric) {
    if (fieldData === null || fieldData === undefined) return 0;
    const { value, display } = extractReportFieldRawForSort(fieldData);
    const n =
      parseReportSortNumeric(value) ?? parseReportSortNumeric(display);
    return n !== null ? n : 0;
  }

  if (fieldData === null || fieldData === undefined) return null;

  const { value, display } = extractReportFieldRawForSort(fieldData);
  let raw =
    value !== undefined && value !== null && value !== ''
      ? value
      : display;

  if (raw === null || raw === undefined || raw === '') return null;

  if (effectiveType === 'date' || effectiveType === 'datetime') {
    const t = new Date(raw).getTime();
    return Number.isFinite(t) ? t : String(raw).toLowerCase();
  }

  if (effectiveType === 'boolean' || effectiveType === 'checkbox') {
    if (raw === true || raw === 'true' || raw === 1 || raw === '1') return 1;
    if (raw === false || raw === 'false' || raw === 0 || raw === '0') return 0;
    return null;
  }

  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'boolean') return raw ? 1 : 0;
  const n = parseReportSortNumeric(raw);
  if (n !== null) return n;
  return String(raw).toLowerCase();
}

export function getReportFieldSummaryTypeOptions(field) {
  const dataType = resolveReportFieldSummaryDataType(field);
  const numericTypes = [
    'number',
    'currency',
    'percent',
    'rollupsummary',
    'autonumber',
  ];
  const dateTypes = ['date', 'datetime'];

  if (numericTypes.includes(dataType)) {
    return [
      { value: 'none', label: 'No Summary' },
      { value: 'sum', label: 'Sum' },
      { value: 'avg', label: 'Average' },
      { value: 'min', label: 'Minimum' },
      { value: 'max', label: 'Maximum' },
      { value: 'count', label: 'Count' },
    ];
  }
  if (dateTypes.includes(dataType)) {
    return [
      { value: 'none', label: 'No Summary' },
      { value: 'min', label: 'Earliest' },
      { value: 'max', label: 'Latest' },
      { value: 'count', label: 'Count' },
    ];
  }
  return [
    { value: 'none', label: 'No Summary' },
    { value: 'count', label: 'Count' },
  ];
}

export function isRightAlignedReportField(field) {
  if (!field) return false;
  const dt = String(field.data_type || '').toLowerCase();
  if (dt === 'calculated') {
    const rt = getCalculatedReturnType(field);
    return ['number', 'currency', 'percent'].includes(rt);
  }
  return isRightAlignedReportDataType(dt);
}

const REPORT_DEFAULT_SUMMARY_TYPES = new Set(['number', 'currency', 'percent']);

/** Implicit Sum footer for tabular columns without an explicit summary. */
export function reportFieldShowsDefaultSum(field) {
  if (!field || field.is_formula) return false;
  return REPORT_DEFAULT_SUMMARY_TYPES.has(resolveReportFieldSummaryDataType(field));
}

/** First column that should show "Grand Total (count)" in a simple tabular footer. */
export function findTabularGrandTotalLabelIndex(fields, summarizedFields = {}) {
  if (!Array.isArray(fields) || fields.length === 0) return 0;
  const idx = fields.findIndex(
    (field) =>
      !field.is_formula &&
      !summarizedFields[field.id] &&
      !reportFieldShowsDefaultSum(field)
  );
  return idx >= 0 ? idx : 0;
}

/** Grand total cell value: explicit aggregation, else default sum for numeric-like fields. */
export function getReportGrandTotalCellValue(
  data,
  field,
  summarizedFields,
  baseCurrencyCode,
  formatAggregatedDisplay,
  exchangeRateLookup = null,
  primaryObjectName = null
) {
  if (!field || field.is_formula) return null;
  const summary = summarizedFields?.[field.id];
  const aggregation = summary?.aggregation;
  if (aggregation && String(aggregation).toLowerCase() !== 'none') {
    return calculateReportAggregation(
      data,
      field,
      aggregation,
      baseCurrencyCode,
      formatAggregatedDisplay,
      exchangeRateLookup,
      primaryObjectName
    );
  }
  if (reportFieldShowsDefaultSum(field)) {
    return calculateReportAggregation(
      data,
      field,
      'sum',
      baseCurrencyCode,
      formatAggregatedDisplay,
      exchangeRateLookup,
      primaryObjectName
    );
  }
  return null;
}

export function getRollupOutputType(field) {
  if (!field) return null;
  const dt = String(field.data_type || '').toLowerCase();
  if (dt !== 'rollupsummary') return null;
  const parsed = parseDisplayConfigForReport(
    field.display_config || field.displayConfig
  );
  const rollupCfg = parsed.rollupSummary || parsed.rollup_summary || parsed;
  return String(
    rollupCfg?.outputType || rollupCfg?.output_type || parsed.outputType || 'number'
  ).toLowerCase();
}

const REPORT_ZERO_WHEN_EMPTY_TYPES = new Set([
  'number',
  'currency',
  'percent',
]);

/** Whether empty/null report cells should display formatted zero instead of N/A. */
export function reportFieldUsesZeroWhenEmpty(field) {
  if (!field) return false;
  const dt = String(field.data_type || '').toLowerCase();
  if (dt === 'calculated') {
    const rt = getCalculatedReturnType(field);
    return REPORT_ZERO_WHEN_EMPTY_TYPES.has(rt);
  }
  if (dt === 'rollupsummary') {
    const outputType = getRollupOutputType(field) || 'number';
    return REPORT_ZERO_WHEN_EMPTY_TYPES.has(outputType);
  }
  return REPORT_ZERO_WHEN_EMPTY_TYPES.has(dt);
}

/**
 * Formatted zero for number / currency / percent / numeric rollup (and calculated with those return types).
 */
export function formatReportZeroDisplay(field, options = {}) {
  const {
    row = null,
    displayConfig = null,
    fallbackCurrencyCode = resolveTokenCurrency('INR'),
    baseCurrencyCode = null,
  } = options;

  const fieldForType = field || {};
  const effectiveType = resolveReportFieldSummaryDataType({
    ...fieldForType,
    display_config:
      displayConfig ||
      fieldForType.display_config ||
      fieldForType.displayConfig,
  });

  const decimalPlaces =
    field?.scale !== undefined && field?.scale !== null && field?.scale !== ''
      ? parseInt(field.scale, 10)
      : 2;
  const fracOpts = {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  };

  if (effectiveType === 'percent' || reportFieldIsPercentLike(fieldForType)) {
    return formatPercent(0, fracOpts);
  }

  if (effectiveType === 'currency' || reportFieldIsCurrencyLike(fieldForType)) {
    const tableName = field?.object_name || 'main';
    const rowCurrency =
      row &&
      getCurrencyCodeFromRecord(row, {
        objectName: tableName,
        fallbackCurrencyCode: null,
      });
    const code =
      (rowCurrency && String(rowCurrency).trim()) ||
      (baseCurrencyCode && String(baseCurrencyCode).trim()) ||
      fallbackCurrencyCode;
    return formatCurrency(0, code, fracOpts);
  }

  return formatNumber(0, fracOpts);
}

/** Empty report cell: formatted zero for numeric types, otherwise N/A. */
export function getReportEmptyDisplay(field, options = {}) {
  const pseudoField =
    field ||
    (options.dataType
      ? {
          data_type: options.dataType,
          display_config: options.displayConfig,
        }
      : null);
  if (!pseudoField || !reportFieldUsesZeroWhenEmpty(pseudoField)) {
    return 'N/A';
  }
  return formatReportZeroDisplay(pseudoField, options);
}

/**
 * Helpers for report subtotals / grand totals / charts when amounts are in record currency
 * and should be aggregated in company base currency (same logic as list/detail views).
 */

export function parseDisplayConfigForReport(displayConfig) {
  if (!displayConfig) return {};
  if (typeof displayConfig === 'string') {
    try {
      return JSON.parse(displayConfig);
    } catch (e) {
      return {};
    }
  }
  return displayConfig || {};
}

/** Rollup measures that typically hold currency (when display_config is missing in builder). */
const ROLLUP_CURRENCY_FIELD_NAMES = new Set([
  'amount',
  'total_amount',
  'totalamount',
  'revenue',
  'deal_amount',
]);

export function enrichReportFieldMetadata(field, catalogField) {
  if (!field) return field;
  const cat = catalogField || field;
  return {
    ...cat,
    ...field,
    display_config: field.display_config ?? cat.display_config,
    displayConfig:
      field.displayConfig ??
      field.display_config ??
      cat.displayConfig ??
      cat.display_config,
    config: field.config ?? cat.config,
    fieldConfig: field.fieldConfig ?? field.config ?? cat.fieldConfig ?? cat.config,
    scale: field.scale ?? cat.scale,
    rollup_field: field.rollup_field ?? cat.rollup_field,
  };
}

export function enrichReportFieldsFromCatalog(fields, catalog) {
  if (!Array.isArray(fields) || !Array.isArray(catalog) || catalog.length === 0) {
    return fields || [];
  }
  const byKey = new Map();
  catalog.forEach(f => {
    if (f?.id != null) byKey.set(`${f.object_name}|${f.id}`, f);
  });
  return fields.map(f => {
    const cat = byKey.get(`${f.object_name}|${f.id}`);
    return cat ? enrichReportFieldMetadata(f, cat) : f;
  });
}

export function reportFieldIsPercentLike(field) {
  if (!field) return false;
  const dt = String(field.data_type || '').toLowerCase();
  if (dt === 'percent') return true;
  if (dt === 'calculated') {
    return getCalculatedReturnType(field) === 'percent';
  }
  if (dt === 'rollupsummary') {
    return getRollupOutputType(field) === 'percent';
  }
  return false;
}

export function reportFieldIsCurrencyLike(field) {
  if (!field) return false;
  const dt = String(field.data_type || '').toLowerCase();
  if (dt === 'calculated') {
    return getCalculatedReturnType(field) === 'currency';
  }
  if (dt === 'currency') return true;
  if (dt === 'rollupsummary') {
    const outputType = getRollupOutputType(field) || 'number';
    if (outputType === 'currency') return true;
    const parsed = parseDisplayConfigForReport(
      field.display_config || field.displayConfig
    );
    const rollupCfg = parsed.rollupSummary || parsed.rollup_summary || parsed;
    const fieldName = String(field.field_name || '').toLowerCase();
    if (ROLLUP_CURRENCY_FIELD_NAMES.has(fieldName)) return true;
    const rollupField = String(
      field.rollup_field || rollupCfg?.rollupField || rollupCfg?.rollup_field || ''
    ).toLowerCase();
    if (ROLLUP_CURRENCY_FIELD_NAMES.has(rollupField)) return true;
  }
  return false;
}

/** Matrix/report row has per-object currency columns (preview API includes opportunity_currency, etc.). */
export function shouldConvertReportMeasureAtRow(field, row, baseCurrencyCode) {
  if (!field || !row || !baseCurrencyCode) return false;
  if (reportFieldIsCurrencyLike(field)) return true;
  const dt = String(field.data_type || '').toLowerCase();
  if (dt !== 'rollupsummary') return false;
  const { currency } = getRowCurrencyAndRateFromReportRow(
    row,
    field.object_name || 'main'
  );
  return Boolean(currency);
}

export function unwrapReportScalar(val) {
  if (val == null || val === '') return null;
  if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
    const inner = val.value ?? val.displayValue;
    if (inner == null || inner === '') return null;
    if (typeof inner === 'object' && inner !== null && !Array.isArray(inner)) {
      return unwrapReportScalar(inner);
    }
    return inner;
  }
  return val;
}

/**
 * Read currency + exchange_rate for a report row for the given object (table alias prefix).
 */
export function getRowCurrencyAndRateFromReportRow(row, objectName) {
  if (!row) {
    return { currency: null, rate: NaN };
  }
  const table = String(objectName || 'main').trim();
  const curKey = `${table}_currency`;
  const rateKey = `${table}_exchange_rate`;
  const rowKeys = Object.keys(row);

  let c = row[curKey] ?? row.currency;
  let r = row[rateKey] ?? row.exchange_rate;

  if (c == null || c === '') {
    const match = rowKeys.find((k) => k.toLowerCase() === curKey.toLowerCase());
    if (match) c = row[match];
  }
  if (r == null || r === '') {
    const match = rowKeys.find((k) => k.toLowerCase() === rateKey.toLowerCase());
    if (match) r = row[match];
  }

  return {
    currency: unwrapReportScalar(c),
    rate: Number(unwrapReportScalar(r)),
  };
}

/**
 * Build map: uppercase currency_code → exchange_rate from Currency API list
 * (same rates as currency_exchange_rate / record exchange_rate).
 */
export function buildExchangeRateLookupFromCurrencyList(list) {
  const lookup = Object.create(null);
  if (!Array.isArray(list)) return lookup;
  for (const row of list) {
    const code = row?.currency_code;
    if (code == null || String(code).trim() === '') continue;
    const rate = Number(row.exchange_rate);
    if (Number.isFinite(rate) && rate > 0) {
      lookup[String(code).trim().toUpperCase()] = rate;
    }
  }
  return lookup;
}

/**
 * Prefer row snapshot rate; if missing/invalid, use current org rate from lookup.
 */
export function resolveEffectiveExchangeRate(currencyCode, rowRate, lookup) {
  if (Number.isFinite(rowRate) && rowRate > 0) return rowRate;
  if (!lookup || currencyCode == null || String(currencyCode).trim() === '') {
    return NaN;
  }
  const key = String(currencyCode).trim().toUpperCase();
  const r = lookup[key];
  return Number.isFinite(r) && r > 0 ? r : NaN;
}

export function convertAmountToBase(amount, currency, rate, baseCode) {
  if (!Number.isFinite(amount)) return amount;
  if (!baseCode || !currency) return amount;
  if (
    String(currency).trim().toUpperCase() ===
    String(baseCode).trim().toUpperCase()
  ) {
    return amount;
  }
  if (!Number.isFinite(rate) || rate <= 0) return amount;
  return amount / rate;
}

export function formatAggregatedNumericForReport(result, field, aggregation = 'sum') {
  if (result == null || !Number.isFinite(Number(result))) {
    return reportFieldUsesZeroWhenEmpty(field)
      ? formatReportZeroDisplay(field)
      : 'N/A';
  }
  const num = Number(result);
  const agg = String(aggregation || 'sum').toLowerCase();
  if (agg === 'count' || field?.field_name === 'record_count') {
    return String(Math.round(num));
  }
  const decimalPlaces =
    field?.scale !== undefined && field?.scale !== null && field?.scale !== ''
      ? parseInt(field.scale, 10)
      : 2;
  const fracOpts = {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  };
  if (reportFieldIsPercentLike(field)) {
    return formatPercent(num, fracOpts);
  }
  if (reportFieldIsCurrencyLike(field)) {
    return formatCurrency(num, resolveTokenCurrency('INR'), fracOpts);
  }
  return formatNumber(num, fracOpts);
}

export function formatAggregatedDisplayForReport(
  result,
  field,
  aggregation,
  baseCurrencyCode,
  formatValueImpl
) {
  if (aggregation === 'count') return String(result);
  if (reportFieldIsPercentLike(field)) {
    const decimalPlaces =
      field.scale !== undefined && field.scale !== null && field.scale !== ''
        ? parseInt(field.scale, 10)
        : 2;
    return formatPercent(result, {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    });
  }
  if (reportFieldIsCurrencyLike(field) && baseCurrencyCode) {
    const decimalPlaces =
      field.scale !== undefined && field.scale !== null && field.scale !== ''
        ? parseInt(field.scale, 10)
        : 2;
    return formatCurrency(result, baseCurrencyCode, {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    });
  }
  return formatValueImpl(result, field, aggregation);
}

function getReportFieldFractionOptions(field) {
  const decimalPlaces =
    field?.scale !== undefined && field?.scale !== null && field?.scale !== ''
      ? parseInt(field.scale, 10)
      : 2;
  return {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  };
}

function aggregateNumericValues(values, aggregation) {
  if (!values?.length) return null;
  const agg = normalizeAggregation(aggregation);
  switch (agg) {
    case 'sum':
      return values.reduce((sum, val) => sum + val, 0);
    case 'average':
    case 'avg':
      return values.reduce((sum, val) => sum + val, 0) / values.length;
    case 'max':
      return Math.max(...values);
    case 'min':
      return Math.min(...values);
    case 'median': {
      const sorted = [...values].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      return sorted.length % 2 === 0
        ? (sorted[mid - 1] + sorted[mid]) / 2
        : sorted[mid];
    }
    case 'count':
      return values.length;
    default:
      return null;
  }
}

function resolveDominantRecordCurrencyForReportRows(
  data,
  field,
  primaryObjectName,
  fallbackCurrencyCode
) {
  const tableName = field?.object_name || primaryObjectName || 'main';
  const counts = new Map();
  for (const row of data || []) {
    const { currency } = getRowCurrencyAndRateFromReportRow(row, tableName);
    const code =
      currency && String(currency).trim()
        ? String(currency).trim().toUpperCase()
        : null;
    if (code) counts.set(code, (counts.get(code) || 0) + 1);
  }
  if (counts.size === 0) {
    return fallbackCurrencyCode
      ? String(fallbackCurrencyCode).trim().toUpperCase()
      : null;
  }
  let dominant = null;
  let maxCount = -1;
  counts.forEach((count, code) => {
    if (count > maxCount) {
      dominant = code;
      maxCount = count;
    }
  });
  return dominant;
}

/**
 * Aggregated currency totals: record-currency sum with symbol, plus base equivalent
 * in parentheses when record currency differs from company base (matches detail cells).
 */
export function formatReportAggregatedCurrencyDisplay(
  data,
  field,
  aggregation,
  baseCurrencyCode,
  exchangeRateLookup = null,
  primaryObjectName = null,
  fallbackCurrencyCode = null
) {
  const fallback =
    (fallbackCurrencyCode && String(fallbackCurrencyCode).trim()) ||
    resolveTokenCurrency('INR');
  const fracOpts = getReportFieldFractionOptions(field);
  const base = baseCurrencyCode
    ? String(baseCurrencyCode).trim().toUpperCase()
    : null;

  const rawValues = collectValuesForAggregation(
    data,
    field,
    aggregation,
    null,
    exchangeRateLookup,
    primaryObjectName
  );
  if (!rawValues?.length) {
    return formatReportZeroDisplay(field, {
      baseCurrencyCode: base,
      fallbackCurrencyCode: fallback,
    });
  }

  const recordResult = aggregateNumericValues(rawValues, aggregation);
  if (recordResult == null || !Number.isFinite(recordResult)) {
    return reportFieldUsesZeroWhenEmpty(field)
      ? formatReportZeroDisplay(field, {
          baseCurrencyCode: base,
          fallbackCurrencyCode: fallback,
        })
      : 'N/A';
  }

  const recordCurrency = resolveDominantRecordCurrencyForReportRows(
    data,
    field,
    primaryObjectName,
    fallback
  );
  const codeForFormat = recordCurrency || fallback;
  let out = formatCurrency(recordResult, codeForFormat, fracOpts);

  if (!base || !recordCurrency || recordCurrency === base) {
    return out;
  }

  const baseValues = collectValuesForAggregation(
    data,
    field,
    aggregation,
    baseCurrencyCode,
    exchangeRateLookup,
    primaryObjectName
  );
  const baseResult = aggregateNumericValues(baseValues, aggregation);
  if (baseResult == null || !Number.isFinite(baseResult)) {
    return out;
  }

  out += ` (${formatCurrency(baseResult, base, fracOpts)} ${base})`;
  return out;
}

function normalizeAggregation(aggregation) {
  if (aggregation == null || aggregation === '') return '';
  const raw = String(aggregation).trim().toLowerCase();
  const aliases = { average: 'avg', mean: 'avg' };
  return aliases[raw] || raw;
}

function collectValuesForAggregation(
  data,
  field,
  aggregation,
  baseCurrencyCode,
  exchangeRateLookup = null,
  primaryObjectName = null
) {
  if (!data || data.length === 0) return null;

  const agg = normalizeAggregation(aggregation);
  const tableName = field.object_name || primaryObjectName || 'main';

  if (agg === 'count') {
    const count = data.filter((row) => {
      const fieldData = resolveReportRowFieldData(
        row,
        field,
        primaryObjectName
      );
      if (fieldData === null || fieldData === undefined || fieldData === '') {
        return false;
      }
      if (typeof fieldData === 'object' && fieldData !== null) {
        const v = fieldData.value ?? fieldData.displayValue;
        return v !== null && v !== undefined && String(v).trim() !== '';
      }
      return true;
    }).length;
    return count > 0 ? new Array(count).fill(1) : null;
  }

  const useBase =
    !!baseCurrencyCode && reportFieldIsCurrencyLike(field) && agg !== 'count';

  const values = data
    .map(row => {
      const fieldData = resolveReportRowFieldData(
        row,
        field,
        primaryObjectName
      );
      const num = parseReportSortNumeric(unwrapReportScalar(fieldData));
      if (num === null) return null;

      if (useBase) {
        const { currency, rate } = getRowCurrencyAndRateFromReportRow(
          row,
          tableName
        );
        const effectiveRate = resolveEffectiveExchangeRate(
          currency,
          rate,
          exchangeRateLookup
        );
        return convertAmountToBase(num, currency, effectiveRate, baseCurrencyCode);
      }
      return num;
    })
    .filter(val => val !== null && !isNaN(val));

  return values.length ? values : null;
}

/** Raw numeric aggregation (for charts). Returns null if N/A. Count returns integer. */
export function getAggregationNumericResult(
  data,
  field,
  aggregation,
  baseCurrencyCode,
  exchangeRateLookup = null,
  primaryObjectName = null
) {
  const values = collectValuesForAggregation(
    data,
    field,
    aggregation,
    baseCurrencyCode,
    exchangeRateLookup,
    primaryObjectName
  );
  if (!values || values.length === 0) return null;

  const agg = normalizeAggregation(aggregation);
  switch (agg) {
    case 'sum':
      return values.reduce((sum, val) => sum + val, 0);
    case 'average':
    case 'avg':
      return values.reduce((sum, val) => sum + val, 0) / values.length;
    case 'max':
      return Math.max(...values);
    case 'min':
      return Math.min(...values);
    case 'median': {
      const sorted = [...values].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      return sorted.length % 2 === 0
        ? (sorted[mid - 1] + sorted[mid]) / 2
        : sorted[mid];
    }
    case 'count':
      return values.length;
    default:
      return null;
  }
}

export function calculateReportAggregation(
  data,
  field,
  aggregation,
  baseCurrencyCode,
  formatAggregatedDisplay,
  exchangeRateLookup = null,
  primaryObjectName = null
) {
  const values = collectValuesForAggregation(
    data,
    field,
    aggregation,
    baseCurrencyCode,
    exchangeRateLookup,
    primaryObjectName
  );
  if (!values || values.length === 0) {
    const agg = normalizeAggregation(aggregation);
    if (agg === 'count') return '0';
    if (reportFieldUsesZeroWhenEmpty(field)) {
      return formatReportZeroDisplay(field, {
        baseCurrencyCode,
      });
    }
    return 'N/A';
  }

  const agg = normalizeAggregation(aggregation);
  let result;
  switch (agg) {
    case 'sum':
      result = values.reduce((sum, val) => sum + val, 0);
      break;
    case 'average':
    case 'avg':
      result = values.reduce((sum, val) => sum + val, 0) / values.length;
      break;
    case 'max':
      result = Math.max(...values);
      break;
    case 'min':
      result = Math.min(...values);
      break;
    case 'median': {
      const sorted = [...values].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      result =
        sorted.length % 2 === 0
          ? (sorted[mid - 1] + sorted[mid]) / 2
          : sorted[mid];
      break;
    }
    case 'count':
      return values.length.toString();
    default:
      return reportFieldUsesZeroWhenEmpty(field)
        ? formatReportZeroDisplay(field, { baseCurrencyCode })
        : 'N/A';
  }

  if (reportFieldIsCurrencyLike(field)) {
    // Multi-currency: totals must be in base currency (converted), not raw face-value sum.
    if (baseCurrencyCode) {
      return formatAggregatedDisplayForReport(
        result,
        field,
        aggregation,
        baseCurrencyCode,
        (res, fld) =>
          formatCurrency(res, baseCurrencyCode, getReportFieldFractionOptions(fld))
      );
    }
    return formatReportAggregatedCurrencyDisplay(
      data,
      field,
      aggregation,
      baseCurrencyCode,
      exchangeRateLookup,
      primaryObjectName
    );
  }

  return formatAggregatedDisplay(result, field, aggregation);
}

/**
 * Single numeric for export subtotals (0 when unparsable; converts to base when applicable).
 */
export function getAggregatableNumericForExport(
  field,
  row,
  baseCurrencyCode,
  getFieldValueForExport,
  exchangeRateLookup = null
) {
  const v = parseFloat(getFieldValueForExport(field, row));
  const n = isNaN(v) ? 0 : v;
  if (!baseCurrencyCode || !reportFieldIsCurrencyLike(field)) return n;
  const { currency, rate } = getRowCurrencyAndRateFromReportRow(
    row,
    field.object_name || 'main'
  );
  const effectiveRate = resolveEffectiveExchangeRate(
    currency,
    rate,
    exchangeRateLookup
  );
  return convertAmountToBase(n, currency, effectiveRate, baseCurrencyCode);
}

function extractNumericFromReportFieldData(fieldData) {
  if (fieldData == null) return null;
  const raw =
    typeof fieldData === 'object' && fieldData !== null
      ? unwrapReportScalar(fieldData) ?? fieldData.value
      : fieldData;
  if (raw === null || raw === undefined || raw === '') return null;
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * Currency / rollup-currency report cells: format in record currency (correct symbol)
 * and append base equivalent when record currency ≠ base and exchange_rate is valid.
 * @returns {string|null} null → caller should use default formatValue
 * @param {Record<string, number>|null} exchangeRateLookup — from buildExchangeRateLookupFromCurrencyList; used when row has no valid exchange_rate
 */
export function formatReportCurrencyCell(
  fieldData,
  field,
  row,
  baseCurrencyCode,
  fallbackCurrencyCode = 'INR',
  exchangeRateLookup = null
) {
  if (!field || !row) return null;
  if (!reportFieldIsCurrencyLike(field)) return null;

  const num = extractNumericFromReportFieldData(fieldData);
  if (num == null) {
    return formatReportZeroDisplay(field, {
      row,
      fallbackCurrencyCode,
      baseCurrencyCode,
    });
  }

  const tableName = field.object_name || 'main';
  const { currency: recordCurrencyRaw, rate: rowRate } =
    getRowCurrencyAndRateFromReportRow(row, tableName);
  const recordCurrency =
    recordCurrencyRaw && String(recordCurrencyRaw).trim()
      ? String(recordCurrencyRaw).trim().toUpperCase()
      : null;

  const decimalPlaces =
    field.scale !== undefined && field.scale !== null && field.scale !== ''
      ? parseInt(field.scale, 10)
      : 2;
  const opts = {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  };

  const codeForFormat = recordCurrency || fallbackCurrencyCode;
  let out = formatCurrency(num, codeForFormat, opts);

  const base = baseCurrencyCode
    ? String(baseCurrencyCode).trim().toUpperCase()
    : null;
  const effectiveRate = resolveEffectiveExchangeRate(
    recordCurrency,
    rowRate,
    exchangeRateLookup
  );
  if (
    base &&
    recordCurrency &&
    recordCurrency !== base &&
    Number.isFinite(effectiveRate) &&
    effectiveRate > 0
  ) {
    const baseEquiv = num / effectiveRate;
    out += ` (${formatCurrency(baseEquiv, base, opts)} ${base})`;
  }

  return out;
}

/**
 * Calculated report cells (including nested calculated formulas evaluated on the server).
 * @returns {string|null} null → caller should use default formatValue
 */
export function formatReportCalculatedCell(
  fieldData,
  field,
  row,
  fallbackCurrencyCode = null
) {
  if (!field) return null;
  const dt = String(field.data_type || '').toLowerCase();
  if (dt !== 'calculated') return null;

  const displayConfig = field.display_config || field.displayConfig;
  const raw =
    typeof fieldData === 'object' && fieldData !== null
      ? toDisplayPrimitive(fieldData)
      : fieldData;
  if (raw === null || raw === undefined || raw === '') {
    return formatReportZeroDisplay(field, {
      row,
      displayConfig,
      fallbackCurrencyCode,
    });
  }

  const objectName = field.object_name || 'main';
  const rowCurrency = getCurrencyCodeFromRecord(row, {
    objectName,
    fallbackCurrencyCode: null,
  });
  const effectiveFallback =
    rowCurrency ||
    (fallbackCurrencyCode && String(fallbackCurrencyCode).trim()) ||
    resolveTokenCurrency('INR');
  const formatOpts = getCalculatedFormatOptions(displayConfig, row, {
    objectName,
    fallbackCurrencyCode: effectiveFallback,
  });
  const formatted = formatCalculatedValue(raw, displayConfig, formatOpts);
  if (formatted === '—') {
    return formatReportZeroDisplay(field, {
      row,
      displayConfig,
      fallbackCurrencyCode: effectiveFallback,
    });
  }
  return formatted;
}
